/**
 * Console superadmin des jobs RQ (PROD1.4 clôture).
 *
 * Vue cross-tenant des jobs interactive/bulk avec compteurs par queue,
 * filtres, et actions cancel/requeue/pause/resume.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  adminJobsService,
  type JobItem,
  type JobStatus,
  type QueueName,
  type QueueStats,
  type WorkerInfo,
} from '../../services/adminJobsService';
import { toastSuccess, toastError } from '../../lib/toast';
import { PageContainer } from '../../components/layout';
import { FilterableTable } from '../../components/ui';
import type { FilterableColumnDef } from '../../components/ui';

const REFRESH_MS = 5000;

const STATUS_LABEL: Record<JobStatus, string> = {
  pending: 'En attente',
  started: 'En cours',
  finished: 'Terminé',
  failed: 'Échec',
};

const STATUS_BADGE: Record<JobStatus, string> = {
  pending: 'bg-gray-100 text-gray-800',
  started: 'bg-blue-100 text-blue-800',
  finished: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleString();
}

function fmtDuration(d: number | null): string {
  if (d === null || d === undefined) return '—';
  if (d < 60) return `${d.toFixed(1)}s`;
  return `${Math.floor(d / 60)}m ${Math.round(d % 60)}s`;
}

export function JobsPage() {
  const [queues, setQueues] = useState<QueueStats[]>([]);
  const [workers, setWorkers] = useState<WorkerInfo[]>([]);
  const [globalPaused, setGlobalPaused] = useState(false);
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filtres
  const [queueFilter, setQueueFilter] = useState<QueueName | ''>('');
  const [statusFilter, setStatusFilter] = useState<JobStatus | ''>('');
  const [search, setSearch] = useState('');

  // Modale d'erreur (traceback)
  const [errorModal, setErrorModal] = useState<JobItem | null>(null);

  const load = useCallback(async () => {
    try {
      const [qstatus, js, ws] = await Promise.all([
        adminJobsService.queuesStatus(),
        adminJobsService.listJobs({
          queue: queueFilter || undefined,
          status: statusFilter || undefined,
          search: search || undefined,
          limit: 100,
        }),
        adminJobsService.listWorkers(),
      ]);
      setQueues(qstatus.queues);
      setGlobalPaused(qstatus.global_paused);
      setJobs(js.items);
      setTotal(js.total);
      setWorkers(ws);
    } catch (e: any) {
      console.error('Erreur chargement jobs:', e);
    } finally {
      setLoading(false);
    }
  }, [queueFilter, statusFilter, search]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const handlePauseToggle = async (paused: boolean) => {
    try {
      if (paused) {
        await adminJobsService.resumeQueues();
        toastSuccess('Workers repris');
      } else {
        await adminJobsService.pauseQueues();
        toastSuccess('Workers en pause');
      }
      load();
    } catch (e) {
      toastError('Action échouée', e);
    }
  };

  const handleCancel = async (jobId: string) => {
    if (!confirm('Annuler ce job ?')) return;
    try {
      await adminJobsService.cancelJob(jobId);
      toastSuccess('Job annulé');
      load();
    } catch (e) {
      toastError('Annulation échouée', e);
    }
  };

  const handlePauseQueue = async (name: QueueName) => {
    try {
      await adminJobsService.pauseQueue(name);
      toastSuccess(`Queue ${name} en pause`);
      load();
    } catch (e) {
      toastError('Pause queue échouée', e);
    }
  };

  const handleResumeQueue = async (name: QueueName) => {
    try {
      await adminJobsService.resumeQueue(name);
      toastSuccess(`Queue ${name} reprise`);
      load();
    } catch (e) {
      toastError('Reprise queue échouée', e);
    }
  };

  const handleStop = async (jobId: string) => {
    if (!confirm('Arrêter ce job en cours d\'exécution ?\n\nLe worker recevra SIGINT (best effort).')) return;
    try {
      await adminJobsService.stopJob(jobId);
      toastSuccess('Signal SIGINT envoyé (best effort)');
      load();
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) {
        toastError('Job non running — impossible de stopper');
        return;
      }
      toastError('Stop échoué', e);
    }
  };

  const handleRequeue = async (jobId: string) => {
    try {
      await adminJobsService.requeueJob(jobId);
      toastSuccess('Job remis en file');
      load();
    } catch (e: any) {
      // Garde-fou backend : si le document a déjà été traité avec succès
      // entre-temps, le requeue est refusé (409) pour éviter d'écraser
      // silencieusement une validation analyste avec une v+1 non sollicitée.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && typeof detail === 'object' && detail?.code === 'document_already_processed') {
        toastError(
          `Document déjà traité (status=${detail.document_status}). ` +
          `Réessayer créerait une v+1 silencieuse. ` +
          `Pour une ré-extraction volontaire, utilisez "Ré-extraire" depuis la fiche document.`
        );
        return;
      }
      toastError('Re-enqueue échouée', e);
    }
  };


  const columns: FilterableColumnDef<JobItem>[] = useMemo(() => [
    {
      key: 'queue',
      header: 'Queue',
      filterable: true,
      filterValueFn: (j) => j.queue,
      filterOptions: [
        { value: 'interactive', label: 'interactive' },
        { value: 'bulk', label: 'bulk' },
      ],
      render: (j) => <span className="text-xs font-mono">{j.queue}</span>,
    },
    {
      key: 'kind',
      header: 'Type',
      filterable: true,
      filterValueFn: (j) => j.kind,
      filterOptions: [
        { value: 'extraction', label: 'extraction' },
        { value: 'poll S3', label: 'poll S3' },
        { value: 'tick scheduled', label: 'tick scheduled' },
        { value: 'benchmark', label: 'benchmark' },
      ],
      render: (j) => (
        <span
          className={`text-xs px-2 py-0.5 rounded ${
            j.is_system ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'
          }`}
        >
          {j.kind}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      filterable: true,
      filterValueFn: (j) => j.status,
      filterOptions: [
        { value: 'pending', label: 'En attente' },
        { value: 'started', label: 'En cours' },
        { value: 'finished', label: 'Terminé' },
        { value: 'failed', label: 'Échec' },
      ],
      render: (j) => (
        <>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[j.status]}`}
          >
            {STATUS_LABEL[j.status]}
          </span>
          {j.status === 'failed' && j.exc_info && (
            <button
              onClick={() => setErrorModal(j)}
              className="ml-2 text-xs text-blue-600 hover:underline"
            >
              Voir erreur
            </button>
          )}
        </>
      ),
    },
    {
      key: 'client_slug',
      header: 'Tenant',
      searchable: true,
      sortable: true,
      sortValueFn: (j) => j.is_system ? 'zzz_system' : (j.client_slug ?? ''),
      render: (j) => (
        <span className="text-xs">
          {j.is_system ? (
            <em className="text-purple-700">système</em>
          ) : (
            j.client_slug ?? <span className="text-gray-400">—</span>
          )}
        </span>
      ),
    },
    {
      key: 'document',
      header: 'Document',
      searchable: true,
      sortValueFn: (j) => j.document_filename ?? j.document_id ?? '',
      render: (j) => (
        <div className="text-xs max-w-[260px]">
          {j.document_id ? (
            <div className="truncate" title={j.document_id}>
              {j.document_filename ? (
                <span>{j.document_filename}</span>
              ) : (
                <span className="font-mono text-gray-500">{j.document_id}</span>
              )}
            </div>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </div>
      ),
    },
    {
      key: 'worker_name',
      header: 'Worker',
      filterable: true,
      filterValueFn: (j) => j.worker_name ?? '',
      filterOptions: [
        { value: 'worker_interactive', label: 'worker_interactive' },
        { value: 'worker_bulk', label: 'worker_bulk' },
        { value: 'worker_benchmark', label: 'worker_benchmark' },
        { value: 'worker_mirador', label: 'worker_mirador' },
      ],
      render: (j) =>
        j.worker_name ? (
          <span className="text-xs font-mono text-gray-600">{j.worker_name}</span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: 'enqueued_at',
      header: 'Enqueued',
      sortable: true,
      sortValueFn: (j) => j.enqueued_at ?? '',
      render: (j) => <span className="text-xs">{fmtDate(j.enqueued_at)}</span>,
    },
    {
      key: 'duration_s',
      header: 'Durée',
      sortable: true,
      sortValueFn: (j) => String(j.duration_s ?? -1).padStart(10, '0'),
      render: (j) => <span className="text-xs">{fmtDuration(j.duration_s)}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (j) => (
        <div className="text-right">
          {j.status === 'pending' && (
            <button
              onClick={() => handleCancel(j.job_id)}
              className="text-xs text-red-600 hover:underline mr-2"
            >
              Annuler
            </button>
          )}
          {j.status === 'started' && (
            <button
              onClick={() => handleStop(j.job_id)}
              className="text-xs text-orange-600 hover:underline mr-2"
            >
              ⏹ Stop
            </button>
          )}
          {j.status === 'failed' && (
            <button
              onClick={() => handleRequeue(j.job_id)}
              className="text-xs text-blue-600 hover:underline"
            >
              Réessayer
            </button>
          )}
        </div>
      ),
    },
  ], []);

  return (
    <PageContainer>
      <h1 className="text-xl font-semibold text-gray-900 mb-4">Jobs RQ</h1>

      {globalPaused && (
        <div
          role="alert"
          className="mb-4 px-4 py-3 rounded border border-red-300 bg-red-50 text-red-900 text-sm flex items-center justify-between gap-3"
        >
          <div>
            <div className="font-medium">🛑 Pause globale RQ active</div>
            <div className="text-xs mt-0.5">
              Aucun worker ne pioche : actions analystes, ticks scheduler, polling S3, benchmarks — tout est suspendu.
              Les jobs en cours d'exécution lors de la pause finissent normalement ; les jobs `pending` s'accumulent.
              <strong className="font-medium"> Inertie ~30s</strong> : un worker en BLPOP Redis peut piocher
              un dernier job juste après l'activation de la pause (limitation RQ standard).
            </div>
          </div>
        </div>
      )}

      {/* Cartes par queue */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        {queues.map((q) => (
          <div key={q.name} className="border rounded-lg p-4 bg-white">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold capitalize">{q.name}</h2>
              {globalPaused ? (
                <span
                  title="La pause globale RQ suspend tous les workers. Reprenez la pause globale pour agir par queue."
                  className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-500 cursor-not-allowed"
                >
                  🔒 Pause globale active
                </span>
              ) : q.paused ? (
                <button
                  onClick={() => handleResumeQueue(q.name)}
                  className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700"
                >
                  ▶ Reprendre {q.name}
                </button>
              ) : (
                <button
                  onClick={() => handlePauseQueue(q.name)}
                  className="text-xs px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700"
                >
                  ⏸ Pause {q.name}
                </button>
              )}
            </div>
            {q.paused && !globalPaused && (
              <div
                role="alert"
                className="mb-2 text-xs px-3 py-2 rounded bg-amber-50 border border-amber-300 text-amber-900 flex items-center justify-between gap-2"
              >
                <span>
                  ⚠ {q.name.charAt(0).toUpperCase() + q.name.slice(1)} en pause
                  {q.paused_at ? ` depuis ${new Date(q.paused_at).toLocaleTimeString()}` : ''}
                  {q.paused_by_email ? ` par ${q.paused_by_email}` : ''}
                </span>
                <button
                  onClick={() => handleResumeQueue(q.name)}
                  className="px-2 py-0.5 rounded bg-amber-200 hover:bg-amber-300 text-amber-900 font-medium"
                >
                  Reprendre
                </button>
              </div>
            )}
            <div className="grid grid-cols-4 gap-2 text-center text-sm">
              <div>
                <div className="text-2xl font-bold text-gray-700">{q.pending}</div>
                <div className="text-xs text-gray-500">en attente</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-700">{q.started}</div>
                <div className="text-xs text-gray-500">en cours</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-700">{q.finished}</div>
                <div className="text-xs text-gray-500">terminés</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-700">{q.failed}</div>
                <div className="text-xs text-gray-500">échecs</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Workers actifs — clarifie quelle queue est servie par quel(s) worker(s).
          Ex. interactive est servie par worker_interactive ET worker_bulk, d'où
          2 jobs interactifs possibles en parallèle. */}
      <div className="mb-4 border rounded-lg bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold">Workers actifs</h2>
          <span className="text-xs text-gray-500">{workers.length} worker(s)</span>
        </div>
        {workers.length === 0 ? (
          <p className="text-xs text-gray-500">Aucun worker détecté.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {workers.map((w) => (
              <div key={w.name} className="border rounded p-2 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono font-medium">{w.name}</span>
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      w.state === 'busy'
                        ? 'bg-blue-100 text-blue-800'
                        : w.state === 'suspended'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {w.state === 'busy' ? 'occupé' : w.state === 'suspended' ? 'suspendu' : 'libre'}
                  </span>
                </div>
                <div className="text-gray-500">
                  queues : <span className="font-mono">{w.queues.join(', ')}</span>
                </div>
                <div className="text-gray-400 mt-0.5">
                  ✓ {w.successful_jobs} · ✗ {w.failed_jobs}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action globale pause */}
      <div className="mb-4 flex items-center gap-3">
        <button
          onClick={() => handlePauseToggle(globalPaused)}
          className={`px-4 py-2 text-sm rounded font-medium ${
            globalPaused
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-amber-600 text-white hover:bg-amber-700'
          }`}
        >
          {globalPaused ? '▶ Reprendre globalement (RQ)' : '⏸ Pause globale RQ (catastrophe)'}
        </button>
        <p className="text-xs text-gray-500">
          Pause globale `rq.suspension` — affecte les 2 workers, à utiliser uniquement
          en cas de catastrophe. Pour pauser une seule queue, utilise les boutons par
          carte ci-dessus (DokimoWorker).
        </p>
      </div>

      {/* Filtres */}
      <div className="mb-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Queue</label>
          <select
            value={queueFilter}
            onChange={(e) => setQueueFilter(e.target.value as QueueName | '')}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value="">Toutes</option>
            <option value="interactive">interactive</option>
            <option value="bulk">bulk</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Statut</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as JobStatus | '')}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value="">Tous</option>
            <option value="pending">En attente</option>
            <option value="started">En cours</option>
            <option value="finished">Terminés</option>
            <option value="failed">Échecs</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Recherche (job_id / document_id)
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
            placeholder="Substring..."
          />
        </div>
        <div className="text-xs text-gray-500 ml-auto">
          Auto-refresh 5s · {total} job(s) trouvé(s)
        </div>
      </div>

      {/* Tableau (PROD1.4.7 #504 : FilterableTable hybride — filtres bandeau côté serveur, tri/filtre colonne côté client) */}
      <div className="bg-white border rounded-lg overflow-hidden">
        {loading ? (
          <div className="text-center py-6 text-gray-500 text-sm">Chargement...</div>
        ) : (
          <FilterableTable
            persistKey="parametres-jobs"
            paginated
            data={jobs}
            columns={columns}
            rowKey={(j) => `${j.queue}:${j.status}:${j.job_id}`}
            defaultSort={{ key: 'enqueued_at', direction: 'desc' }}
            noDataMessage="Aucun job pour ces filtres."
            emptyMessage="Aucun job ne correspond aux filtres"
          />
        )}
      </div>

      {/* Modale traceback */}
      {errorModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setErrorModal(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-3xl w-full mx-4 max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">
                Traceback — job {errorModal.job_id.slice(0, 8)}…
              </h3>
              <button
                onClick={() => setErrorModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <pre className="bg-gray-900 text-gray-100 text-xs p-3 rounded overflow-x-auto whitespace-pre-wrap">
              {errorModal.exc_info}
            </pre>
          </div>
        </div>
      )}
    </PageContainer>
  );
}

export default JobsPage;
